import * as React from "react";
import isEmpty from "lodash/isEmpty";
import { Selectors } from "@grandvisionhq/auth";
import { Context } from "@grandvisionhq/carts-v2";
import { toast, Loader } from "@grandvisionhq/common";
import { Util } from "@grandvisionhq/forms";
import { useLink } from "@grandvisionhq/frk-routing";
import { Alert } from "@grandvisionhq/iris-ui";
import { useSync } from "@grandvisionhq/lib-utils-w3c";
import { WithUserData } from "@grandvisionhq/my-account";
import { ExpressPaymentMethod, ApplePayButton, GooglePayButton } from "@grandvisionhq/payments";
import { useFeatureToggles, useDispatch, useSelector, useIntl } from "@grandvisionhq/state";
import { init, toggleAccountCreation } from "../../../../../../actions.js";
import { pushCheckoutEventToDataLayer, pushExpressCheckoutSubmitEventToDataLayer } from "../../../../../../data-layer.js";
import { useSyncCartShippingMethodWithReduxAfterTabFocus } from "../../../../../../hooks/carts/hooks/carts/use-sync-cart-shipping-method-with-redux.js";
import { useExpressCheckoutLabels } from "../../../../../../hooks/common/hooks/common/use-express-checkout-labels.js";
import { LoaderModal } from "../../../../../LoaderModal/modules/LoaderModal/LoaderModal.js";
import { getCheckout, getServiceStore, shouldCreateAccount, isInitialized } from "../../../../../../selectors.js";
import { selectShippingMethod, toggleExpressCheckout, markFieldsTouched, setMyAccountValidation, setServiceStoreValidation } from "../../../../../../StepShipping/StepShipping/actions.js";
import { isExpressCheckoutValid } from "../../../../../../StepShipping/StepShipping/selectors.js";
import { Header } from "../../../../../Header/modules/Header/Header.js";
import { ExpressCheckoutUI } from "./ExpressCheckoutUI.js";
/* empty css                              */
import { ShippingMethod } from "../../../../../../models/step-shipping/models/step-shipping/shipping-methods.js";
import { useHandleExpressPayment } from "../../../../hooks/modules/ExpressCheckout/hooks/use-handle-express-payment.js";
import { CheckoutStepErrorType } from "../../../../../../models/checkout/models/checkout/checkout-step-error-type.js";
import { useCheckoutSettings } from "../../../../../../context/CheckoutSettings/context/CheckoutSettings/context.js";
const ExpressCheckoutContainer = ({
  type,
  backButton,
  shippingMethodsAsides,
  selfUrl,
  status
}) => {
  const dispatch = useDispatch();
  const checkoutState = useSelector(getCheckout);
  const { getTitle } = useExpressCheckoutLabels();
  const { cart, error, called, loading } = Context.useCart();
  const checkoutSettings = useCheckoutSettings();
  const selectedServiceStore = useSelector(getServiceStore);
  const isLoggedIn = useSelector(Selectors.isLoggedIn);
  const shouldCreateAccountBoolean = useSelector(shouldCreateAccount);
  const isExpressCheckoutValidBoolean = useSelector(isExpressCheckoutValid);
  const isNearestPickup = cart?.shippingMethod.key === ShippingMethod.NearestPickup;
  const intl = useIntl();
  const isCheckoutEventFired = React.useRef(false);
  const { onClick: redirectToCartPage, href: cartLink } = useLink({ route: "cart.overview" }, { replace: true }) || {};
  const expressCheckoutRouteIsSupported = type === ExpressPaymentMethod.APPLE_PAY || type === ExpressPaymentMethod.GOOGLE_PAY;
  const expressCheckoutLink = useLink({
    route: "express-checkout",
    params: {
      type: expressCheckoutRouteIsSupported ? type : ExpressPaymentMethod.GOOGLE_PAY,
      returnPath: cartLink,
      cartId: cart?.id
    }
  });
  const [payCartWithExpressPaymentMethod, orderInProcess] = useHandleExpressPayment({
    type,
    selfUrl,
    returnPath: expressCheckoutRouteIsSupported ? expressCheckoutLink?.href : cartLink
  });
  const isServiceStoreEnabled = checkoutSettings.features?.isServiceStoreEnabled;
  const isServiceStoreRequired = Boolean(
    isServiceStoreEnabled && !checkoutSettings.serviceStore?.isOptional
  );
  const broadCastChannel = useSync("new-cart-created").getChannel();
  broadCastChannel.onmessage = () => {
    toast.failed("cart.errors.cartNotInActiveState");
    redirectToCartPage?.();
  };
  useSyncCartShippingMethodWithReduxAfterTabFocus();
  const isCreateAccountRequired = Boolean(
    checkoutSettings.account?.implicitAccountCreation || checkoutSettings.account?.createAccountRequired
  );
  const isFailedCheckoutStep = status === CheckoutStepErrorType.Failed;
  React.useEffect(() => {
    if (!loading && cart && !isCheckoutEventFired.current) {
      const checkoutMethod = type === ExpressPaymentMethod.APPLE_PAY ? "express_checkout_applepay" : "express_checkout_googlepay";
      pushCheckoutEventToDataLayer(cart, intl.currency, checkoutMethod);
      isCheckoutEventFired.current = true;
    }
  }, [cart, intl.currency, loading, type]);
  const { onClick: redirectToHomePage } = useLink({ route: "home" }, { replace: true }) || {};
  React.useEffect(() => {
    if (!isInitialized({ checkout: checkoutState })) {
      dispatch(init());
    }
  }, [dispatch, checkoutState, checkoutSettings.routes, selfUrl]);
  React.useEffect(() => {
    !isCreateAccountRequired && dispatch(
      toggleAccountCreation({
        shouldCreateAccount: false
      })
    );
  }, [isCreateAccountRequired]);
  React.useEffect(() => {
    dispatch(
      selectShippingMethod({
        shippingMethod: { method: ShippingMethod.Delivery, isValid: true, isTouched: true }
      })
    );
    dispatch(
      toggleExpressCheckout({
        enabled: true
      })
    );
    return () => {
      dispatch(
        toggleExpressCheckout({
          enabled: false
        })
      );
    };
  }, [dispatch]);
  const isStepValid = React.useCallback(() => {
    const serviceStoreIsValid = isServiceStoreRequired ? !isEmpty(selectedServiceStore) : true;
    return isExpressCheckoutValidBoolean && serviceStoreIsValid && (isLoggedIn || !isCreateAccountRequired || shouldCreateAccountBoolean);
  }, [
    isExpressCheckoutValidBoolean,
    isServiceStoreRequired,
    selectedServiceStore,
    isLoggedIn,
    isCreateAccountRequired,
    shouldCreateAccountBoolean
  ]);
  const handleExpressCheckoutSubmitEvent = React.useCallback(() => {
    cart && pushExpressCheckoutSubmitEventToDataLayer({ cart, paymentMethod: type, isLoggedIn });
  }, [cart, isLoggedIn, type]);
  const onCheckoutClick = React.useCallback(() => {
    if (isStepValid() && payCartWithExpressPaymentMethod) {
      payCartWithExpressPaymentMethod();
      handleExpressCheckoutSubmitEvent();
    } else {
      dispatch(markFieldsTouched());
      if (isCreateAccountRequired && !shouldCreateAccountBoolean) {
        dispatch(setMyAccountValidation());
      }
      if (isServiceStoreEnabled && !checkoutSettings.serviceStore?.isOptional) {
        dispatch(setServiceStoreValidation());
      }
      toast.failed("checkout.errors.form.general");
      Util.focusToTopErrorFieldOnScreen();
    }
  }, [
    dispatch,
    handleExpressCheckoutSubmitEvent,
    isCreateAccountRequired,
    isServiceStoreEnabled,
    isStepValid,
    shouldCreateAccountBoolean,
    redirectToCartPage,
    checkoutSettings.serviceStore?.isOptional
  ]);
  React.useEffect(() => {
    if (!cart?.lineItems?.length && !loading && called) {
      console.error(`Need a valid cart in localstorage or a cart with products.`);
      redirectToHomePage?.();
    }
  }, [cart, called, loading, redirectToHomePage]);
  return /* @__PURE__ */ React.createElement("div", { className: "express-checkout-container" }, /* @__PURE__ */ React.createElement(
    LoaderModal,
    {
      isModalOpen: Boolean(orderInProcess),
      subheading: intl.getLabel("checkout.order.inprogress")
    }
  ), /* @__PURE__ */ React.createElement(Header, { title: getTitle(type), backButton }), /* @__PURE__ */ React.createElement("section", { className: "express-checkout-container__inner" }, /* @__PURE__ */ React.createElement("main", { className: "express-checkout-container__main" }, isFailedCheckoutStep && /* @__PURE__ */ React.createElement(
    Alert,
    {
      className: "alert-m-default checkout-section__alert",
      variant: "warning",
      title: intl.getLabel("checkout.errors.general.title")
    },
    intl.getLabel("checkout.errors.general.message")
  ), (loading || error) && /* @__PURE__ */ React.createElement("section", { className: "express-checkout-container__loader" }, /* @__PURE__ */ React.createElement(Loader, null)), cart && /* @__PURE__ */ React.createElement(
    ExpressCheckoutUI,
    {
      shippingMethodsAsides,
      isNearestPickup
    }
  )), /* @__PURE__ */ React.createElement("footer", { className: "express-checkout-container__footer" }, cart && type === ExpressPaymentMethod.APPLE_PAY && !orderInProcess && /* @__PURE__ */ React.createElement(ApplePayButton, { type: "express-checkout", onClick: onCheckoutClick }), cart && type === ExpressPaymentMethod.GOOGLE_PAY && !orderInProcess && /* @__PURE__ */ React.createElement(GooglePayButton, { type: "express-checkout", onClick: onCheckoutClick }))));
};
const ExpressCheckoutContainerWithUserData = WithUserData(ExpressCheckoutContainer);
const ExpressCheckoutContainerWithMyAccount = ({
  ...props
}) => {
  const { isMyAccountEnabled } = useFeatureToggles();
  return /* @__PURE__ */ React.createElement(ExpressCheckoutContainerWithUserData, { ...props, isMyAccountEnabled });
};
export {
  ExpressCheckoutContainerWithMyAccount as default
};
//# sourceMappingURL=ExpressCheckoutContainer.js.map
